/**
 * src/pages/OriginAndBeyond/sections/CosmicSpiralCanvas.tsx
 * Signature visual: expanding spiral galaxy & light-from-a-point motif.
 * Features:
 * - HTML5 Canvas with gentle rotational drift
 * - Lighter computation on mobile (fewer particles, lower DPR)
 * - Pauses rendering when scrolled out of view via IntersectionObserver
 * - Full prefers-reduced-motion check: renders a single elegant static frame without loop
 */

import React, { useEffect, useRef } from "react";
import { useReducedMotion } from "@/components/cosmic/useReducedMotion";
export function CosmicSpiralCanvas() {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const prefersReduced = useReducedMotion();
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    let animationFrameId: number;
    let isVisible = true;
    let width = 0;
    let height = 0;
    let angle = 0;
    const isMobile = window.innerWidth < 768;
    const particleCount = isMobile ? 120 : 260;
    const dpr = Math.min(window.devicePixelRatio || 1, isMobile ? 1.25 : 1.75);

    // Generate static particle coordinates in logarithmic spiral distributions
    interface SpiralParticle {
      radius: number;
      baseAngle: number;
      speed: number;
      size: number;
      alpha: number;
      color: string; // cyan, laser-blue, or violet
    }
    const particles: SpiralParticle[] = [];
    const colorPalette = ["rgba(0, 240, 255, ",
    // Electric cyan
    "rgba(56, 189, 248, ",
    // Laser sky blue
    "rgba(168, 85, 247, ",
    // Cosmic violet
    "rgba(255, 255, 255, " // Starlight white
    ];
    for (let i = 0; i < particleCount; i++) {
      const arm = i % 3; // 3-arm spiral
      const armOffset = arm * 2 * Math.PI / 3;
      const progress = Math.pow(Math.random(), 0.7); // clustered toward center
      const radius = progress * 380 + 15;
      const spiralTurn = Math.log(radius + 1) * 2.8;
      const jitter = (Math.random() - 0.5) * 0.45;
      particles.push({
        radius,
        baseAngle: armOffset + spiralTurn + jitter,
        speed: (0.15 + Math.random() * 0.25) / Math.max(1, radius * 0.05),
        size: Math.random() * (isMobile ? 1.5 : 2.2) + 0.6,
        alpha: Math.random() * 0.6 + 0.3,
        color: colorPalette[Math.floor(Math.random() * colorPalette.length)]
      });
    }
    const handleResize = () => {
      if (!canvas || !containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      width = rect.width;
      height = rect.height;
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
    };
    handleResize();
    window.addEventListener("resize", handleResize);

    // Pause rendering when scrolled out of view
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      isVisible = entry.isIntersecting;
    }, {
      threshold: 0.05
    });
    if (containerRef.current) {
      observer.observe(containerRef.current);
    }
    const render = () => {
      ctx.save();
      ctx.scale(dpr, dpr);
      ctx.clearRect(0, 0, width, height);
      const centerX = width / 2;
      const centerY = height / 2;

      // 1. Central primordial singularity glow
      const coreGradient = ctx.createRadialGradient(centerX, centerY, 0, centerX, centerY, Math.min(width, height) * 0.45);
      coreGradient.addColorStop(0, "rgba(0, 240, 255, 0.22)");
      coreGradient.addColorStop(0.25, "rgba(168, 85, 247, 0.12)");
      coreGradient.addColorStop(0.65, "rgba(2, 6, 23, 0.05)");
      coreGradient.addColorStop(1, "rgba(2, 6, 23, 0)");
      ctx.fillStyle = coreGradient;
      ctx.beginPath();
      ctx.arc(centerX, centerY, Math.min(width, height) * 0.45, 0, Math.PI * 2);
      ctx.fill();

      // 2. Render spiral particles
      for (let i = 0; i < particles.length; i++) {
        const p = particles[i];
        const currentAngle = p.baseAngle + (prefersReduced ? 0 : angle * p.speed);
        const x = centerX + Math.cos(currentAngle) * p.radius;
        const y = centerY + Math.sin(currentAngle) * (p.radius * 0.62); // slight elliptical perspective

        ctx.fillStyle = `${p.color}${p.alpha})`;
        ctx.beginPath();
        ctx.arc(x, y, p.size, 0, Math.PI * 2);
        ctx.fill();

        // Extra specular starlight point for brightest particles
        if (p.size > 2) {
          ctx.fillStyle = "rgba(255, 255, 255, 0.75)";
          ctx.beginPath();
          ctx.arc(x, y, p.size * 0.45, 0, Math.PI * 2);
          ctx.fill();
        }
      }
      ctx.restore();
    };

    // If reduced motion is requested, render once and finish
    if (prefersReduced) {
      render();
      return () => {
        window.removeEventListener("resize", handleResize);
        observer.disconnect();
      };
    }
    const loop = () => {
      if (isVisible) {
        angle += 0.004;
        render();
      }
      animationFrameId = requestAnimationFrame(loop);
    };
    loop();
    return () => {
      cancelAnimationFrame(animationFrameId);
      window.removeEventListener("resize", handleResize);
      observer.disconnect();
    };
  }, [prefersReduced]);
  return <div ref={containerRef} className="absolute inset-0 w-full h-full pointer-events-none overflow-hidden select-none" aria-hidden="true">
      <canvas ref={canvasRef} className="block w-full h-full opacity-80" />
      {/* Radial depth vignette scrim */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_25%,var(--background)_80%)] pointer-events-none" />
    </div>;
}